import { createContext, useContext } from 'react';

/** Ask Tia: a page of the open Library Document. */
export const PAGE_CITATION_HREF_PREFIX = '#document-page-';

/** Connie chat: a page of a named Library Document (VH3_CONNECT #549). */
export const LIBRARY_PAGE_HREF_PREFIX = '#library-page-';

/**
 * A Page Citation as FSI writes it: `[<Library Document name>, p. <N>]`. The same
 * pattern as `api/tia/page_reading.py` `parse_page_citations`. Global, so use it with
 * `replace` or `matchAll`, which never share `lastIndex`.
 */
export const PAGE_CITATION_PATTERN =
  /\[(?<name>[^[\]\n]+?),\s*p\.\s*(?<page>\d{1,5})\]/g;

/**
 * FSI's `citation_names_match` rule: case and extra spaces ignored, `[` and `]`
 * read as `(` and `)`.
 */
export const normalizeCitationName = (name: string) =>
  name
    .replace(/\[/g, '(')
    .replace(/]/g, ')')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

export const citationNamesMatch = (
  cited: string | undefined,
  documentName: string | undefined
) => {
  const key = normalizeCitationName(documentName ?? '');
  return key.length > 0 && normalizeCitationName(cited ?? '') === key;
};

export type PageCitationContextValue = {
  onOpenPage: (page: number) => void;
};

export const PageCitationContext =
  createContext<PageCitationContextValue | null>(null);

export const usePageCitation = () => useContext(PageCitationContext);

export const parsePageCitationHref = (href?: string): number | null => {
  if (!href?.startsWith(PAGE_CITATION_HREF_PREFIX)) return null;
  const page = Number(href.slice(PAGE_CITATION_HREF_PREFIX.length));
  return Number.isInteger(page) && page > 0 ? page : null;
};

const LIBRARY_PAGE_HREF =
  /^#library-page-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-(\d{1,5})$/i;

export const libraryPageHref = (documentId: string, page: number) =>
  `${LIBRARY_PAGE_HREF_PREFIX}${documentId}-${page}`;

export const parseLibraryPageHref = (
  href?: string
): { documentId: string; page: number } | null => {
  const match = href?.match(LIBRARY_PAGE_HREF);
  if (!match) return null;
  const page = Number(match[2]);
  return page > 0 ? { documentId: match[1], page } : null;
};

/**
 * A Page Citation that may name several pages, as Connie sometimes writes it:
 * `[Name, p. 5]`, `[Name, pp. 1, 2, 5]`, `[Name, pp. 2-4]`, `[Name, p. 3 and 4]`.
 * Connie chat accepts these; Ask Tia keeps the strict one-page form above.
 */
const PAGE_SPAN = String.raw`\d{1,5}(?:\s*(?:-|–|to)\s*\d{1,5})?`;
export const PAGE_CITATION_LIST_PATTERN = new RegExp(
  String.raw`\[(?<name>[^[\]\n]+?),\s*(?<pages>p{1,2}\.\s*${PAGE_SPAN}(?:\s*(?:,|&|and)\s*${PAGE_SPAN})*)\]`,
  'g'
);

const MAX_RANGE = 20;

/** The page numbers a `pages` group names, in order; a range expands to at most 20. */
export const citationPageNumbers = (spec: string): number[] => {
  const pages: number[] = [];
  const add = (page: number) => {
    if (page > 0 && !pages.includes(page)) pages.push(page);
  };
  for (const part of spec
    .replace(/^p{1,2}\.\s*/, '')
    .split(/\s*(?:,|&|and)\s*/)) {
    const bounds = (part.match(/\d{1,5}/g) ?? []).map(Number);
    if (
      bounds.length === 2 &&
      bounds[1] >= bounds[0] &&
      bounds[1] - bounds[0] < MAX_RANGE
    ) {
      for (let page = bounds[0]; page <= bounds[1]; page += 1) add(page);
    } else {
      bounds.forEach(add);
    }
  }
  return pages;
};

/**
 * The name without what Connie tends to drop: file extensions, a Library Import's
 * ` - 28_09_2026_ 16_54` upload stamp, a ` (1)` copy mark.
 */
export const looseCitationName = (name: string) =>
  normalizeCitationName(name)
    .replace(/(?:\.(?:pdf|docx?|xlsx?|pptx?|txt|md))+$/i, '')
    .replace(/\s*\(\d+\)$/, '')
    .replace(/\s*-\s*\d{1,2}_\d{1,2}_\d{4}_?\s*\d{1,2}_\d{2}$/, '')
    .replace(/(?:\.(?:pdf|docx?|xlsx?|pptx?|txt|md))+$/i, '')
    .replace(/[\s\-_:]+$/, '')
    .trim();

/**
 * FSI's rule first; failing that, one name is the start of the other once both
 * are stripped of what Connie drops, and the shorter is long enough to be safe.
 */
export const citationNamesMatchLoosely = (
  cited: string | undefined,
  documentName: string | undefined
) => {
  if (citationNamesMatch(cited, documentName)) return true;
  const a = looseCitationName(cited ?? '');
  const b = looseCitationName(documentName ?? '');
  if (!a || !b) return false;
  if (a === b) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return short.length >= 12 && long.startsWith(short);
};
